import { useCallback } from "react";
import { Api } from "../../common/service/Api";
import { usePrimaryOrganizationId } from "./useOrganization";
import {
  TrueScanResultsDto,
  TrueScanTrackerAcknowledgement,
} from "../../common/service/server/dto/TrueScanResultsDto";
import { CookieCategory } from "../../common/service/server/dto/CookieScanDto";
import { UUIDString } from "../../common/service/server/Types";

export const useTrueScan = () => {
  const organizationId = usePrimaryOrganizationId();

  const getScan: (rebake?: boolean) => Promise<TrueScanResultsDto> = useCallback(
    async (rebake = false) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      return await Api.trueScan.getLatestTrueScanResults(organizationId, rebake);
    },
    [organizationId],
  );

  const runScan = useCallback(
    async (websiteUrl: string) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      try {
        const response = await Api.trueScan.runTrueScan(organizationId, websiteUrl);
        return { success: response.success };
      } catch (error) {
        console.error("Error running TrueScan:", error);
        return { success: false };
      }
    },
    [organizationId],
  );

  const updateTrackerAcknowledgement = useCallback(
    async (trackerId: string, acknowledgement: TrueScanTrackerAcknowledgement) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      try {
        const response = await Api.trueScan.updateTracker(organizationId, trackerId, {
          acknowledgement,
        });
        return { success: response.success };
      } catch (error) {
        console.error("Error updating tracker acknowledgement:", error);
        return { success: false };
      }
    },
    [organizationId],
  );

  const updateTrackerConsentCategories = useCallback(
    async (trackerId: string, consentCategories: CookieCategory[]) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      try {
        const response = await Api.trueScan.updateTracker(organizationId, trackerId, {
          consentCategories,
        });
        return { success: response.success };
      } catch (error) {
        console.error("Error updating tracker consent categories:", error);
        return { success: false };
      }
    },
    [organizationId],
  );

  const updateTrackerManagedByVendor = useCallback(
    async (trackerId: UUIDString, managedByVendor: UUIDString | null) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      try {
        const response = await Api.trueScan.updateTracker(organizationId, trackerId, {
          managedByVendorUpdate: {
            update: true,
            vendorId: managedByVendor,
          },
        });
        return { success: response.success };
      } catch (error) {
        console.error("Error updating tracker managed by vendor:", error);
        return { success: false };
      }
    },
    [organizationId],
  );

  const updateTrackersInBulk = useCallback(
    async (
      updates: Array<{
        trackerId: string;
        update: {
          consentCategories?: CookieCategory[];
          acknowledgement?: TrueScanTrackerAcknowledgement;
          managedByVendorUpdate?: { update: boolean; vendorId: UUIDString | null };
        };
      }>,
    ) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      try {
        const response = await Api.trueScan.updateTrackersInBulk(organizationId, updates);
        return { success: response.success };
      } catch (error) {
        console.error("Error updating trackers in bulk:", error);
        return { success: false };
      }
    },
    [organizationId],
  );

  const downloadCsv = useCallback(
    async (rebake = false) => {
      if (!organizationId) throw new Error("Organization ID is not available");
      try {
        const blob = await Api.trueScan.downloadCsv(organizationId, rebake);
        const { downloadCsvBlob } = await import("../../common/util/fileDownload");
        downloadCsvBlob(blob, "truescan-results");
        return { success: true };
      } catch (error) {
        console.error("Error downloading TrueScan CSV:", error);
        return { success: false };
      }
    },
    [organizationId],
  );

  return {
    getScan,
    runScan,
    updateTrackerAcknowledgement,
    updateTrackerConsentCategories,
    updateTrackerManagedByVendor,
    updateTrackersInBulk,
    downloadCsv,
  };
};
